import { useEffect, useRef, useState } from "react";
import { FiAlertCircle, FiFilter, FiX } from "react-icons/fi";
import { MdTrendingUp } from "react-icons/md";
import * as XLSX from "xlsx";
import BotaoRelatorio from "../../../components/relatorios/BotaoRelatorio";
import CabecalhoRelatorio from "../../../components/relatorios/CabecalhoRelatorio";
import Cartao from "../../../components/relatorios/Cartao";
import CartaoEstatistica from "../../../components/relatorios/CartaoEstatistica";
import EstadoVazio from "../../../components/relatorios/EstadoVazio";
import { api, listarTudo } from "../../../services/api";
import { exportarRelatorioPDF, formatarPeriodoFiltro } from "../../../utils/relatorioPdf";

const RelatorioRentabilidade = () => {
  const [dados, setDados] = useState({
    resumo: {
      receita_total: 0,
      custo_total: 0,
      lucro: 0,
      margem: 0
    },
    por_cultura: [],
    por_talhao: [],
    por_atividade: [],
    por_tipo: {
      agricultura: { receita: 0, custo: 0, lucro: 0, margem: 0 },
      pecuaria: { receita: 0, custo: 0, lucro: 0, margem: 0 }
    }
  });
  const [showModal, setShowModal] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro] = useState("");
  const [abaAtiva, setAbaAtiva] = useState("resumo"); // resumo, cultura, talhao, atividade, tipo

  const [culturas, setCulturas] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [atividades, setAtividades] = useState([]);
  const [fazendas, setFazendas] = useState([]);
  const [safras, setSafras] = useState([]);

  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRefs = {
    culturas: useRef(null),
    talhoes: useRef(null),
    atividades: useRef(null),
    fazendas: useRef(null),
    safras: useRef(null)
  };

  const [filtros, setFiltros] = useState({
    culturas: [],
    talhoes: [],
    atividades: [],
    fazendas: [],
    safras: [],
    data_inicio: "",
    data_fim: "",
    tipo: "todos" // agricultura, pecuaria, todos
  });

  useEffect(() => {
    carregarOpcoes();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdown && dropdownRefs[openDropdown]?.current && !dropdownRefs[openDropdown].current.contains(event.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const carregarOpcoes = async () => {
    try {
      const [culturasRes, talhoesRes, atividadesRes, fazendasRes, safrasRes] = await Promise.all([
        api.get("/agricultura/culturas/"),
        api.get("/fazendas/talhoes/"),
        listarTudo("/agricultura/tipoatividade/"),
        api.get("/fazendas/fazendas/"),
        api.get("/agricultura/safras/")
      ]);

      setCulturas(culturasRes.data.results || []);
      setTalhoes(talhoesRes.data.results || []);
      setAtividades(Array.isArray(atividadesRes.data) ? atividadesRes.data : atividadesRes.data.results || []);
      setFazendas(fazendasRes.data.results || []);
      setSafras(safrasRes.data.results || []);
    } catch (error) {
      console.error("Erro ao carregar opções:", error);
      setCulturas([]);
      setTalhoes([]);
      setAtividades([]);
      setFazendas([]);
      setSafras([]);
    }
  };

  const handleMultiSelect = (campo, id) => {
    setFiltros(prev => {
      const current = prev[campo] || [];
      const updated = current.includes(id)
        ? current.filter(item => item !== id)
        : [...current, id];
      return { ...prev, [campo]: updated };
    });
  };

  const handleSelectAll = (campo, options) => {
    if (Array.isArray(options)) {
      const allIds = options.map(opt => opt.id);
      setFiltros(prev => ({ ...prev, [campo]: allIds }));
    }
  };

  const handleClearAll = (campo) => {
    setFiltros(prev => ({ ...prev, [campo]: [] }));
  };

  const handleChange = (e) => {
    setFiltros({
      ...filtros,
      [e.target.name]: e.target.value
    });
  };

  const buscarDados = async () => {
    setCarregando(true);
    setErro("");

    try {
      const params = {};

      if (filtros.culturas && filtros.culturas.length > 0) {
        params.culturas = filtros.culturas.join(",");
      }
      if (filtros.talhoes && filtros.talhoes.length > 0) {
        params.talhoes = filtros.talhoes.join(",");
      }
      if (filtros.atividades && filtros.atividades.length > 0) {
        params.atividades = filtros.atividades.join(",");
      }
      if (filtros.fazendas && filtros.fazendas.length > 0) {
        params.fazendas = filtros.fazendas.join(",");
      }
      if (filtros.safras && filtros.safras.length > 0) {
        params.safras = filtros.safras.join(",");
      }
      if (filtros.data_inicio) {
        params.data_inicio = filtros.data_inicio;
      }
      if (filtros.data_fim) {
        params.data_fim = filtros.data_fim;
      }
      if (filtros.tipo && filtros.tipo !== "todos") {
        params.tipo = filtros.tipo;
      }

      console.log("🌐 URL:", `/relatorios/financeiro/rentabilidade/?${new URLSearchParams(params)}`);

      const response = await api.get("/relatorios/financeiro/rentabilidade/", {
        params: params
      });

      console.log("🔍 Dados recebidos:", response.data);
      setDados(response.data);
      setShowModal(true);
    } catch (error) {
      console.error(error);
      setErro("Erro ao gerar o relatório. Verifique os filtros e tente novamente.");
    } finally {
      setCarregando(false);
    }
  };

  const limparFiltros = () => {
    setFiltros({
      culturas: [],
      talhoes: [],
      atividades: [],
      fazendas: [],
      safras: [],
      data_inicio: "",
      data_fim: "",
      tipo: "todos"
    });
  };

  const formatarMoeda = (valor) => {
    if (!valor && valor !== 0) return "-";
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  };

  const formatarPorcentagem = (valor) => {
    if (!valor && valor !== 0) return "-";
    return `${valor.toFixed(1)}%`;
  };

  const exportarExcel = () => {
    if (!dados) {
      alert("Nao ha dados para exportar!");
      return;
    }

    const workbook = XLSX.utils.book_new();

    // Planilha de Resumo
    const wsResumo = XLSX.utils.json_to_sheet([{
      "Receita Total": formatarMoeda(dados.resumo?.receita_total),
      "Custo Total": formatarMoeda(dados.resumo?.custo_total),
      "Lucro": formatarMoeda(dados.resumo?.lucro),
      "Margem": formatarPorcentagem(dados.resumo?.margem)
    }]);
    XLSX.utils.book_append_sheet(workbook, wsResumo, "Resumo");

    // Planilha por Cultura
    if (dados.por_cultura && dados.por_cultura.length > 0) {
      const wsCultura = XLSX.utils.json_to_sheet(dados.por_cultura);
      XLSX.utils.book_append_sheet(workbook, wsCultura, "Por Cultura");
    }

    // Planilha por Talhão
    if (dados.por_talhao && dados.por_talhao.length > 0) {
      const wsTalhao = XLSX.utils.json_to_sheet(dados.por_talhao);
      XLSX.utils.book_append_sheet(workbook, wsTalhao, "Por Talhao");
    }

    // Planilha por Atividade
    if (dados.por_atividade && dados.por_atividade.length > 0) {
      const wsAtividade = XLSX.utils.json_to_sheet(dados.por_atividade);
      XLSX.utils.book_append_sheet(workbook, wsAtividade, "Por Atividade");
    }

    XLSX.writeFile(workbook, "relatorio_rentabilidade.xlsx");
  };

  // O relatório original exportava, num único PDF paginado, as três tabelas
  // (por tipo, por cultura, por talhão) de uma vez. O utilitário compartilhado
  // exporta uma tabela por vez, então a exportação passou a seguir a aba
  // selecionada no modal (mesma lista de abas já usada na tela) — o que já
  // está visível é o que sai no PDF. Isso também passou a cobrir a aba "Por
  // Atividade", que antes não tinha exportação em PDF nenhuma.
  const exportarPDF = async () => {
    if (!dados) {
      alert("Nao ha dados para exportar!");
      return;
    }

    const secoes = {
      resumo: {
        titulo: "Relatório de Rentabilidade - Resumo Geral",
        colunas: [
          { key: "indicador", label: "Indicador" },
          { key: "valor", label: "Valor" },
        ],
        dados: [
          { indicador: "Receita Total", valor: formatarMoeda(dados.resumo?.receita_total) },
          { indicador: "Custo Total", valor: formatarMoeda(dados.resumo?.custo_total) },
          { indicador: "Lucro", valor: formatarMoeda(dados.resumo?.lucro) },
          { indicador: "Margem", valor: formatarPorcentagem(dados.resumo?.margem) },
        ],
      },
      tipo: {
        titulo: "Relatório de Rentabilidade - Agricultura vs Pecuária",
        colunas: [
          { key: "tipo", label: "Tipo" },
          { key: "receita", label: "Receita" },
          { key: "custo", label: "Custo" },
          { key: "lucro", label: "Lucro" },
          { key: "margem", label: "Margem" },
        ],
        dados: [
          {
            tipo: "Agricultura",
            receita: formatarMoeda(dados.por_tipo?.agricultura?.receita),
            custo: formatarMoeda(dados.por_tipo?.agricultura?.custo),
            lucro: formatarMoeda(dados.por_tipo?.agricultura?.lucro),
            margem: formatarPorcentagem(dados.por_tipo?.agricultura?.margem),
          },
          {
            tipo: "Pecuária",
            receita: formatarMoeda(dados.por_tipo?.pecuaria?.receita),
            custo: formatarMoeda(dados.por_tipo?.pecuaria?.custo),
            lucro: formatarMoeda(dados.por_tipo?.pecuaria?.lucro),
            margem: formatarPorcentagem(dados.por_tipo?.pecuaria?.margem),
          },
        ],
      },
      cultura: {
        titulo: "Relatório de Rentabilidade - Por Cultura",
        colunas: [
          { key: "cultura", label: "Cultura" },
          { key: "receita", label: "Receita" },
          { key: "custo", label: "Custo" },
          { key: "lucro", label: "Lucro" },
          { key: "margem", label: "Margem" },
        ],
        dados: (dados.por_cultura || []).map((item) => ({
          cultura: item.cultura || "-",
          receita: formatarMoeda(item.receita),
          custo: formatarMoeda(item.custo),
          lucro: formatarMoeda(item.lucro),
          margem: formatarPorcentagem(item.margem),
        })),
      },
      talhao: {
        titulo: "Relatório de Rentabilidade - Por Talhão",
        colunas: [
          { key: "talhao", label: "Talhão" },
          { key: "cultura", label: "Cultura" },
          { key: "receita", label: "Receita" },
          { key: "custo", label: "Custo" },
          { key: "lucro", label: "Lucro" },
          { key: "margem", label: "Margem" },
        ],
        dados: (dados.por_talhao || []).map((item) => ({
          talhao: item.talhao || "-",
          cultura: item.cultura || "-",
          receita: formatarMoeda(item.receita),
          custo: formatarMoeda(item.custo),
          lucro: formatarMoeda(item.lucro),
          margem: formatarPorcentagem(item.margem),
        })),
      },
      atividade: {
        titulo: "Relatório de Rentabilidade - Por Atividade",
        colunas: [
          { key: "atividade", label: "Atividade" },
          { key: "receita", label: "Receita" },
          { key: "custo", label: "Custo" },
          { key: "lucro", label: "Lucro" },
          { key: "margem", label: "Margem" },
        ],
        dados: (dados.por_atividade || []).map((item) => ({
          atividade: item.atividade || "-",
          receita: formatarMoeda(item.receita),
          custo: formatarMoeda(item.custo),
          lucro: formatarMoeda(item.lucro),
          margem: formatarPorcentagem(item.margem),
        })),
      },
    };

    const secao = secoes[abaAtiva] || secoes.resumo;

    await exportarRelatorioPDF({
      titulo: secao.titulo,
      colunas: secao.colunas,
      dados: secao.dados,
      periodo: formatarPeriodoFiltro(filtros.data_inicio, filtros.data_fim),
    });
  };

  const imprimir = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Relatorio de Rentabilidade</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            h1 { color: #333; font-size: 20px; }
            h2 { color: #555; font-size: 16px; margin-top: 25px; }
            h3 { color: #666; font-size: 14px; margin-top: 20px; }
            .header-info { margin-bottom: 20px; }
            .periodo { background: #e8f4f8; padding: 10px; border-radius: 5px; margin-bottom: 20px; }
            .resumo-cards { display: flex; gap: 20px; margin-bottom: 30px; flex-wrap: wrap; }
            .card { background: #f8f9fa; padding: 20px; border-radius: 10px; text-align: center; min-width: 150px; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }
            .card .valor { font-size: 28px; font-weight: bold; margin-top: 10px; }
            .card .label { font-size: 12px; color: #666; }
            .card.receita .valor { color: #28a745; }
            .card.custo .valor { color: #dc3545; }
            .card.lucro-positivo .valor { color: #28a745; }
            .card.lucro-negativo .valor { color: #dc3545; }
            .card.margem .valor { color: #17a2b8; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; margin-bottom: 25px; font-size: 11px; }
            th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
            th { background-color: #f2f2f2; font-weight: bold; }
            .positivo { color: green; font-weight: bold; }
            .negativo { color: red; font-weight: bold; }
            .footer { margin-top: 30px; font-size: 10px; color: #666; text-align: center; }
            @media print {
              body { margin: 0; padding: 20px; }
              .card, table { break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>Relatorio de Rentabilidade</h1>
          <div class="header-info">
            <p><strong>Gerado em:</strong> ${new Date().toLocaleString()}</p>
          </div>

          ${filtros.data_inicio || filtros.data_fim ? `
          <div class="periodo">
            <strong>📅 Periodo analisado:</strong> ${filtros.data_inicio || 'inicio'} a ${filtros.data_fim || 'atual'}
          </div>
          ` : ''}

          <div class="resumo-cards">
            <div class="card receita">
              <div class="label">💰 Receita Total</div>
              <div class="valor">${formatarMoeda(dados.resumo?.receita_total)}</div>
            </div>
            <div class="card custo">
              <div class="label">📉 Custo Total</div>
              <div class="valor">${formatarMoeda(dados.resumo?.custo_total)}</div>
            </div>
            <div class="card ${(dados.resumo?.lucro || 0) >= 0 ? 'lucro-positivo' : 'lucro-negativo'}">
              <div class="label">📊 Lucro</div>
              <div class="valor">${formatarMoeda(dados.resumo?.lucro)}</div>
            </div>
            <div class="card margem">
              <div class="label">📈 Margem</div>
              <div class="valor">${formatarPorcentagem(dados.resumo?.margem)}</div>
            </div>
          </div>

          <h2>📊 Comparativo Agricultura vs Pecuaria</h2>
          <table>
            <thead><tr><th>Tipo</th><th>Receita</th><th>Custo</th><th>Lucro</th><th>Margem</th></tr></thead>
            <tbody>
              <tr>
                <td>🌱 Agricultura</td>
                <td>${formatarMoeda(dados.por_tipo?.agricultura?.receita)}</td>
                <td>${formatarMoeda(dados.por_tipo?.agricultura?.custo)}</td>
                <td class="${(dados.por_tipo?.agricultura?.lucro || 0) >= 0 ? 'positivo' : 'negativo'}">${formatarMoeda(dados.por_tipo?.agricultura?.lucro)}</td>
                <td>${formatarPorcentagem(dados.por_tipo?.agricultura?.margem)}</td>
              </tr>
              <tr>
                <td>🐄 Pecuaria</td>
                <td>${formatarMoeda(dados.por_tipo?.pecuaria?.receita)}</td>
                <td>${formatarMoeda(dados.por_tipo?.pecuaria?.custo)}</td>
                <td class="${(dados.por_tipo?.pecuaria?.lucro || 0) >= 0 ? 'positivo' : 'negativo'}">${formatarMoeda(dados.por_tipo?.pecuaria?.lucro)}</td>
                <td>${formatarPorcentagem(dados.por_tipo?.pecuaria?.margem)}</td>
              </tr>
            </tbody>
          </table>

          <h2>🌾 Rentabilidade por Cultura</h2>
          <table>
            <thead><tr><th>Cultura</th><th>Receita</th><th>Custo</th><th>Lucro</th><th>Margem</th></tr></thead>
            <tbody>
              ${dados.por_cultura?.map(item => `
                <tr>
                  <td>${item.cultura || '-'}</td>
                  <td>${formatarMoeda(item.receita)}</td>
                  <td>${formatarMoeda(item.custo)}</td>
                  <td class="${(item.lucro || 0) >= 0 ? 'positivo' : 'negativo'}">${formatarMoeda(item.lucro)}</td>
                  <td>${formatarPorcentagem(item.margem)}</td>
                </tr>
              `).join('') || '<tr><td colspan="5">Nenhum dado encontrado</td></tr>'}
            </tbody>
          </table>

          <h2>📍 Rentabilidade por Talhao</h2>
          <table>
            <thead><tr><th>Talhao</th><th>Cultura</th><th>Receita</th><th>Custo</th><th>Lucro</th><th>Margem</th></tr></thead>
            <tbody>
              ${dados.por_talhao?.map(item => `
                <tr>
                  <td>${item.talhao || '-'}</td>
                  <td>${item.cultura || '-'}</td>
                  <td>${formatarMoeda(item.receita)}</td>
                  <td>${formatarMoeda(item.custo)}</td>
                  <td class="${(item.lucro || 0) >= 0 ? 'positivo' : 'negativo'}">${formatarMoeda(item.lucro)}</td>
                  <td>${formatarPorcentagem(item.margem)}</td>
                </tr>
              `).join('') || '<tr><td colspan="6">Nenhum dado encontrado</td></tr>'}
            </tbody>
          </table>

          <div class="footer">Sistema de Gestao Agropecuaria - Relatorio Gerado Automaticamente</div>
          <script>
            window.onload = () => { window.print(); setTimeout(() => window.close(), 100); }
          <\/script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  // Componente de Select Multiplo
  const MultiSelect = ({ label, options, selectedValues, fieldName, refProp }) => {
    const optionsArray = Array.isArray(options) ? options : [];
    const selectedArray = Array.isArray(selectedValues) ? selectedValues : [];

    const getSelectedNames = () => {
      const selected = optionsArray.filter(opt => selectedArray.includes(opt.id));
      if (selected.length === 0) return `Selecione ${label.toLowerCase()}`;
      if (selected.length <= 2) return selected.map(s => s.nome || s.nome_codigo).join(", ");
      return `${selected.length} ${label.toLowerCase()} selecionados`;
    };

    return (
      <div className="relative" ref={refProp}>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          {label}
        </label>
        <div
          onClick={() => setOpenDropdown(openDropdown === fieldName ? null : fieldName)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white cursor-pointer focus:ring-2 focus:ring-brand-green focus:border-transparent"
        >
          <span className="text-gray-700">{getSelectedNames()}</span>
          <span className="float-right">▼</span>
        </div>

        {openDropdown === fieldName && (
          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
            <div className="p-2 border-b border-gray-200 bg-gray-50">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleClearAll(fieldName);
                }}
                className="text-sm text-blue-600 hover:text-blue-800 mr-3"
              >
                Limpar todos
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectAll(fieldName, optionsArray);
                }}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Selecionar todos
              </button>
            </div>
            {optionsArray.length === 0 ? (
              <div className="px-3 py-2 text-gray-500 text-sm">
                Nenhuma opção disponível
              </div>
            ) : (
              optionsArray.map((option) => (
                <label
                  key={option.id}
                  className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedArray.includes(option.id)}
                    onChange={() => handleMultiSelect(fieldName, option.id)}
                    className="mr-3 h-4 w-4 text-brand-green rounded border-gray-300"
                  />
                  <span className="text-gray-700">{option.nome || option.nome_codigo}</span>
                </label>
              ))
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 p-4 md:p-8">
        <div className="max-w-7xl mx-auto">
          <CabecalhoRelatorio
            icone={MdTrendingUp}
            titulo="Relatório de Rentabilidade"
            descricao="Analise a rentabilidade do negócio por cultura, talhão, atividade e segmento"
          />

          {/* Filtros */}
          <Cartao titulo="Filtros" icone={FiFilter} className="mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Tipo */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Segmento
                </label>
                <select
                  name="tipo"
                  value={filtros.tipo}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition bg-white"
                >
                  <option value="todos">Todos</option>
                  <option value="agricultura">Agricultura</option>
                  <option value="pecuaria">Pecuaria</option>
                </select>
              </div>

              {/* Safras - Multi Select */}
              <MultiSelect
                label="Safras"
                options={safras}
                selectedValues={filtros.safras}
                fieldName="safras"
                refProp={dropdownRefs.safras}
              />

              {/* Culturas - Multi Select */}
              <MultiSelect
                label="Culturas"
                options={culturas}
                selectedValues={filtros.culturas}
                fieldName="culturas"
                refProp={dropdownRefs.culturas}
              />

              {/* Talhoes - Multi Select */}
              <MultiSelect
                label="Talhoes"
                options={talhoes}
                selectedValues={filtros.talhoes}
                fieldName="talhoes"
                refProp={dropdownRefs.talhoes}
              />

              {/* Atividades - Multi Select */}
              <MultiSelect
                label="Atividades"
                options={atividades}
                selectedValues={filtros.atividades}
                fieldName="atividades"
                refProp={dropdownRefs.atividades}
              />

              {/* Fazendas - Multi Select */}
              <MultiSelect
                label="Fazendas"
                options={fazendas}
                selectedValues={filtros.fazendas}
                fieldName="fazendas"
                refProp={dropdownRefs.fazendas}
              />

              {/* Data Inicio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Inicio
                </label>
                <input
                  type="date"
                  name="data_inicio"
                  value={filtros.data_inicio}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>

              {/* Data Fim */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Data Fim
                </label>
                <input
                  type="date"
                  name="data_fim"
                  value={filtros.data_fim}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-green focus:border-transparent outline-none transition"
                />
              </div>
            </div>

            {/* Filtros Ativos - Tags */}
            {((filtros.culturas && filtros.culturas.length > 0) ||
              (filtros.talhoes && filtros.talhoes.length > 0) ||
              (filtros.atividades && filtros.atividades.length > 0) ||
              (filtros.fazendas && filtros.fazendas.length > 0) ||
              (filtros.safras && filtros.safras.length > 0)) && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Filtros Selecionados
                </label>
                <div className="flex flex-wrap gap-2">
                  {filtros.culturas && filtros.culturas.map((id) => {
                    const cultura = culturas.find(c => c.id === id);
                    return cultura ? (
                      <span key={`cultura-${id}`} className="bg-green-100 text-green-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Cultura: {cultura.nome}
                        <button onClick={() => handleMultiSelect("culturas", id)}>×</button>
                      </span>
                    ) : null;
                  })}
                  {filtros.talhoes && filtros.talhoes.map((id) => {
                    const talhao = talhoes.find(t => t.id === id);
                    return talhao ? (
                      <span key={`talhao-${id}`} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Talhao: {talhao.nome_codigo}
                        <button onClick={() => handleMultiSelect("talhoes", id)}>×</button>
                      </span>
                    ) : null;
                  })}
                  {filtros.atividades && filtros.atividades.map((id) => {
                    const atividade = atividades.find(a => a.id === id);
                    return atividade ? (
                      <span key={`atividade-${id}`} className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Atividade: {atividade.nome}
                        <button onClick={() => handleMultiSelect("atividades", id)}>×</button>
                      </span>
                    ) : null;
                  })}
                  {filtros.safras && filtros.safras.map((id) => {
                    const safra = safras.find(s => s.id === id);
                    return safra ? (
                      <span key={`safra-${id}`} className="bg-purple-100 text-purple-800 px-2 py-1 rounded-md text-sm flex items-center gap-1">
                        Safra: {safra.nome}
                        <button onClick={() => handleMultiSelect("safras", id)}>×</button>
                      </span>
                    ) : null;
                  })}
                </div>
              </div>
            )}

            {erro && (
              <div className="mt-4 flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                <FiAlertCircle size={18} className="shrink-0" />
                <span>{erro}</span>
              </div>
            )}

            {/* Botoes */}
            <div className="flex gap-3 mt-6">
              <BotaoRelatorio
                variante="primario"
                onClick={buscarDados}
                carregando={carregando}
                className="flex-1"
              >
                Gerar Relatório
              </BotaoRelatorio>
              <BotaoRelatorio variante="secundario" onClick={limparFiltros}>
                Limpar Filtros
              </BotaoRelatorio>
            </div>
          </Cartao>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9999,
            overflow: 'auto'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl my-8 flex flex-col"
            style={{
              zIndex: 10000,
              maxHeight: 'calc(100vh - 4rem)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                <MdTrendingUp size={22} className="text-brand-green" />
                Relatório de Rentabilidade
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition"
              >
                <FiX size={18} className="text-gray-600" />
              </button>
            </div>

            {/* Abas */}
            <div className="border-b border-gray-200 px-6">
              <div className="flex gap-4 overflow-x-auto">
                <button
                  onClick={() => setAbaAtiva("resumo")}
                  className={`py-3 px-4 font-medium transition whitespace-nowrap ${abaAtiva === "resumo" ? "border-b-2 border-brand-green text-brand-green" : "text-gray-500 hover:text-gray-700"}`}
                >
                  📊 Resumo Geral
                </button>
                <button
                  onClick={() => setAbaAtiva("cultura")}
                  className={`py-3 px-4 font-medium transition whitespace-nowrap ${abaAtiva === "cultura" ? "border-b-2 border-green-500 text-green-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  🌾 Por Cultura
                </button>
                <button
                  onClick={() => setAbaAtiva("talhao")}
                  className={`py-3 px-4 font-medium transition whitespace-nowrap ${abaAtiva === "talhao" ? "border-b-2 border-blue-500 text-blue-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  📍 Por Talhão
                </button>
                <button
                  onClick={() => setAbaAtiva("atividade")}
                  className={`py-3 px-4 font-medium transition whitespace-nowrap ${abaAtiva === "atividade" ? "border-b-2 border-yellow-500 text-yellow-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  🔧 Por Atividade
                </button>
                <button
                  onClick={() => setAbaAtiva("tipo")}
                  className={`py-3 px-4 font-medium transition whitespace-nowrap ${abaAtiva === "tipo" ? "border-b-2 border-purple-500 text-purple-600" : "text-gray-500 hover:text-gray-700"}`}
                >
                  ⚖️ Agricultura vs Pecuária
                </button>
              </div>
            </div>

            {/* Acoes */}
            <div className="p-6 border-b border-gray-200 bg-gray-50 flex flex-wrap gap-3 flex-shrink-0">
              <BotaoRelatorio variante="excel" onClick={exportarExcel}>
                Exportar para Excel
              </BotaoRelatorio>
              <BotaoRelatorio variante="pdf" onClick={exportarPDF}>
                Exportar para PDF
              </BotaoRelatorio>
              <BotaoRelatorio variante="imprimir" onClick={imprimir}>
                Imprimir
              </BotaoRelatorio>
            </div>

            {/* Conteudo */}
            <div className="flex-1 overflow-auto p-6" style={{ minHeight: 0 }}>
              {/* Aba Resumo */}
              {abaAtiva === "resumo" && (
                <div>
                  {/* Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
                    <CartaoEstatistica
                      rotulo="Receita Total"
                      valor={formatarMoeda(dados.resumo?.receita_total)}
                      cor="verde"
                    />
                    <CartaoEstatistica
                      rotulo="Custo Total"
                      valor={formatarMoeda(dados.resumo?.custo_total)}
                      cor="vermelho"
                    />
                    <CartaoEstatistica
                      rotulo="Lucro"
                      valor={formatarMoeda(dados.resumo?.lucro)}
                      cor={(dados.resumo?.lucro || 0) >= 0 ? "azul" : "dourado"}
                    />
                    <CartaoEstatistica
                      rotulo="Margem"
                      valor={formatarPorcentagem(dados.resumo?.margem)}
                      cor="roxo"
                    />
                  </div>

                  {/* Grafico de Barras Simples */}
                  <Cartao titulo="Comparativo Receita vs Custo">
                    <div className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Receita</span>
                          <span>{formatarMoeda(dados.resumo?.receita_total)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4">
                          <div className="bg-green-600 h-4 rounded-full" style={{ width: `${Math.min((dados.resumo?.receita_total / (dados.resumo?.receita_total || 1)) * 100, 100)}%` }}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Custo</span>
                          <span>{formatarMoeda(dados.resumo?.custo_total)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4">
                          <div className="bg-red-600 h-4 rounded-full" style={{ width: `${Math.min((dados.resumo?.custo_total / (dados.resumo?.receita_total || 1)) * 100, 100)}%` }}></div>
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Lucro</span>
                          <span className={(dados.resumo?.lucro || 0) >= 0 ? 'text-green-600' : 'text-red-600'}>{formatarMoeda(dados.resumo?.lucro)}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-4">
                          <div className={`h-4 rounded-full ${(dados.resumo?.lucro || 0) >= 0 ? 'bg-blue-600' : 'bg-orange-600'}`} style={{ width: `${Math.abs((dados.resumo?.lucro / (dados.resumo?.receita_total || 1)) * 100)}%` }}></div>
                        </div>
                      </div>
                    </div>
                  </Cartao>
                </div>
              )}

              {/* Aba Por Cultura */}
              {abaAtiva === "cultura" && (
                <div>
                  {dados.por_cultura && dados.por_cultura.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">CULTURA</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">RECEITA</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">CUSTO</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">LUCRO</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">MARGEM</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dados.por_cultura?.map((item, idx) => (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition">
                              <td className="px-4 py-3 text-sm font-medium text-gray-700">{item.cultura || "-"}</td>
                              <td className="px-4 py-3 text-sm text-green-600">{formatarMoeda(item.receita)}</td>
                              <td className="px-4 py-3 text-sm text-red-600">{formatarMoeda(item.custo)}</td>
                              <td className={`px-4 py-3 text-sm font-semibold ${(item.lucro || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                                {formatarMoeda(item.lucro)}
                              </td>
                              <td className={`px-4 py-3 text-sm font-semibold ${(item.margem || 0) >= 0 ? 'text-purple-600' : 'text-orange-600'}`}>
                                {formatarPorcentagem(item.margem)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EstadoVazio descricao="Ajuste os filtros selecionados e tente novamente." />
                  )}
                </div>
              )}

              {/* Aba Por Talhão */}
              {abaAtiva === "talhao" && (
                <div>
                  {dados.por_talhao && dados.por_talhao.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">TALHAO</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">CULTURA</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">RECEITA</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">CUSTO</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">LUCRO</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">MARGEM</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dados.por_talhao?.map((item, idx) => (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition">
                              <td className="px-4 py-3 text-sm text-gray-600">{item.talhao || "-"}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{item.cultura || "-"}</td>
                              <td className="px-4 py-3 text-sm text-green-600">{formatarMoeda(item.receita)}</td>
                              <td className="px-4 py-3 text-sm text-red-600">{formatarMoeda(item.custo)}</td>
                              <td className={`px-4 py-3 text-sm font-semibold ${(item.lucro || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                                {formatarMoeda(item.lucro)}
                              </td>
                              <td className={`px-4 py-3 text-sm font-semibold ${(item.margem || 0) >= 0 ? 'text-purple-600' : 'text-orange-600'}`}>
                                {formatarPorcentagem(item.margem)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EstadoVazio descricao="Ajuste os filtros selecionados e tente novamente." />
                  )}
                </div>
              )}

              {/* Aba Por Atividade */}
              {abaAtiva === "atividade" && (
                <div>
                  {dados.por_atividade && dados.por_atividade.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-200">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-200">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">ATIVIDADE</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">RECEITA</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">CUSTO</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">LUCRO</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">MARGEM</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dados.por_atividade?.map((item, idx) => (
                            <tr key={idx} className="border-b border-gray-100 hover:bg-gray-50 transition">
                              <td className="px-4 py-3 text-sm text-gray-600">{item.atividade || "-"}</td>
                              <td className="px-4 py-3 text-sm text-green-600">{formatarMoeda(item.receita)}</td>
                              <td className="px-4 py-3 text-sm text-red-600">{formatarMoeda(item.custo)}</td>
                              <td className={`px-4 py-3 text-sm font-semibold ${(item.lucro || 0) >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                                {formatarMoeda(item.lucro)}
                              </td>
                              <td className={`px-4 py-3 text-sm font-semibold ${(item.margem || 0) >= 0 ? 'text-purple-600' : 'text-orange-600'}`}>
                                {formatarPorcentagem(item.margem)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <EstadoVazio descricao="Ajuste os filtros selecionados e tente novamente." />
                  )}
                </div>
              )}

              {/* Aba Agricultura vs Pecuária */}
              {abaAtiva === "tipo" && (
                <div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-green-50 rounded-xl p-6 border border-green-200">
                      <h4 className="text-lg font-semibold text-green-800 mb-4">🌱 Agricultura</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span>Receita:</span>
                          <span className="font-bold text-green-600">{formatarMoeda(dados.por_tipo?.agricultura?.receita)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Custo:</span>
                          <span className="font-bold text-red-600">{formatarMoeda(dados.por_tipo?.agricultura?.custo)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Lucro:</span>
                          <span className={`font-bold ${(dados.por_tipo?.agricultura?.lucro || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatarMoeda(dados.por_tipo?.agricultura?.lucro)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Margem:</span>
                          <span className="font-bold text-purple-600">{formatarPorcentagem(dados.por_tipo?.agricultura?.margem)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
                      <h4 className="text-lg font-semibold text-blue-800 mb-4">🐄 Pecuária</h4>
                      <div className="space-y-3">
                        <div className="flex justify-between">
                          <span>Receita:</span>
                          <span className="font-bold text-green-600">{formatarMoeda(dados.por_tipo?.pecuaria?.receita)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Custo:</span>
                          <span className="font-bold text-red-600">{formatarMoeda(dados.por_tipo?.pecuaria?.custo)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Lucro:</span>
                          <span className={`font-bold ${(dados.por_tipo?.pecuaria?.lucro || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatarMoeda(dados.por_tipo?.pecuaria?.lucro)}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span>Margem:</span>
                          <span className="font-bold text-purple-600">{formatarPorcentagem(dados.por_tipo?.pecuaria?.margem)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex justify-end">
              <BotaoRelatorio variante="secundario" onClick={() => setShowModal(false)}>
                Fechar
              </BotaoRelatorio>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default RelatorioRentabilidade;
